import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { api } from '@/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, User, Mail, Calendar, Shield, Trash2, Loader2, CheckCircle, Key } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

function toDateInputValue(value) {
  if (!value) return '';
  // If backend sends ISO string, normalize to YYYY-MM-DD
  try {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  } catch (_) {}
  // If already YYYY-MM-DD
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return '';
}

export default function CustomerProfile() {
  const navigate = useNavigate();
  const { customer, updateProfile, deleteAccount, logout } = useCustomerAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [firstName, setFirstName] = useState(customer?.firstName || '');
  const [lastName, setLastName] = useState(customer?.lastName || '');
  const [dateOfBirth, setDateOfBirth] = useState(toDateInputValue(customer?.dateOfBirth));

  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [twoFaSetup, setTwoFaSetup] = useState(null); // { qrCode, secret }
  const [twoFaCode, setTwoFaCode] = useState('');
  const [twoFaDisablePassword, setTwoFaDisablePassword] = useState('');

  useEffect(() => {
    if (!customer) return;
    setFirstName(customer.firstName || '');
    setLastName(customer.lastName || '');
    setDateOfBirth(toDateInputValue(customer.dateOfBirth));
  }, [customer?.id, customer?.firstName, customer?.lastName, customer?.dateOfBirth]);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  async function handleUpdateProfile(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await updateProfile({
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        dateOfBirth: dateOfBirth || undefined,
      });

      toast({
        title: 'Profil aktualisiert',
        description: 'Deine Änderungen wurden gespeichert.',
      });
    } catch (err) {
      setError(err.message || 'Aktualisierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }

  async function start2FASetup() {
    setTwoFaLoading(true);
    setError(null);
    try {
      const d = await api.customer2faSetup();
      if (d?.ok) {
        setTwoFaSetup({ qrCode: d.qrCode, secret: d.secret });
        setTwoFaCode('');
      }
    } catch (err) {
      setError(err.message || '2FA Setup fehlgeschlagen');
    } finally {
      setTwoFaLoading(false);
    }
  }

  async function enable2FA() {
    if (!twoFaCode || twoFaCode.length !== 6) {
      setError('Bitte gib den 6-stelligen Code ein');
      return;
    }
    setTwoFaLoading(true);
    setError(null);
    try {
      const d = await api.customer2faEnable(twoFaCode);
      if (d?.ok) {
        toast({ title: '2FA aktiviert' });
        setTwoFaSetup(null);
        setTwoFaCode('');
        try { window.dispatchEvent(new Event('customer:refresh')); } catch (_) {}
      }
    } catch (err) {
      setError(err.message || '2FA Aktivierung fehlgeschlagen');
    } finally {
      setTwoFaLoading(false);
    }
  }

  async function disable2FA() {
    if (!twoFaDisablePassword) {
      setError('Bitte Passwort eingeben');
      return;
    }
    setTwoFaLoading(true);
    setError(null);
    try {
      const d = await api.customer2faDisable(twoFaDisablePassword);
      if (d?.ok) {
        toast({ title: '2FA deaktiviert' });
        setTwoFaDisablePassword('');
        try { window.dispatchEvent(new Event('customer:refresh')); } catch (_) {}
      }
    } catch (err) {
      setError(err.message || '2FA Deaktivierung fehlgeschlagen');
    } finally {
      setTwoFaLoading(false);
    }
  }

  async function handleDeleteAccount() {
    if (deleteConfirmText !== 'LÖSCHEN') {
      setError('Bitte gib "LÖSCHEN" ein um fortzufahren');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await deleteAccount();
      toast({
        title: 'Account gelöscht',
        description: 'Dein Account wurde erfolgreich gelöscht.',
      });
      navigate('/');
    } catch (err) {
      setError(err.message || 'Löschung fehlgeschlagen');
      setLoading(false);
    }
  }

  if (!customer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Link to="/customer/dashboard">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Zurück
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Mein Profil</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Verwalte deine persönlichen Daten
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Account Info */}
        <Card>
          <CardHeader>
            <CardTitle>Account-Informationen</CardTitle>
            <CardDescription>
              Deine eindeutige Benutzer-ID und E-Mail-Adresse
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-foreground">UUID</Label>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-muted rounded border border-border font-mono text-sm">
                  {customer.uuid}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(customer.uuid);
                    toast({
                      title: 'UUID kopiert',
                      description: 'Die UUID wurde in die Zwischenablage kopiert.',
                    });
                  }}
                >
                  Kopieren
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Diese ID wird für Support-Anfragen verwendet
              </p>
            </div>

            <div>
              <Label className="text-sm font-medium text-foreground">E-Mail</Label>
              <div className="mt-1 flex items-center gap-2 text-foreground">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span>{customer.email}</span>
                {customer.emailVerified && (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                )}
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium text-foreground">Account-Status</Label>
              <div className="mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  customer.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                  customer.status === 'BLOCKED' ? 'bg-red-100 text-red-800' :
                  'bg-muted text-foreground'
                }`}>
                  {customer.status}
                </span>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium text-foreground">Registriert seit</Label>
              <div className="mt-1 flex items-center gap-2 text-foreground">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>{new Date(customer.createdAt).toLocaleDateString('de-DE')}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Form */}
        <Card>
          <CardHeader>
            <CardTitle>Persönliche Daten</CardTitle>
            <CardDescription>
              Optionale Angaben für personalisierte E-Mails
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first-name">Vorname</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="first-name"
                      type="text"
                      placeholder="Max"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="pl-10"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last-name">Nachname</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="last-name"
                      type="text"
                      placeholder="Mustermann"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="pl-10"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="date-of-birth">Geburtsdatum</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="date-of-birth"
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Änderungen speichern
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Security */}
        <Card>
          <CardHeader>
            <CardTitle>Sicherheit</CardTitle>
            <CardDescription>
              Verwalte dein Passwort und Sicherheitseinstellungen
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/customer/reset-password">
              <Button variant="outline" className="w-full justify-start">
                <Key className="h-4 w-4 mr-2" />
                Passwort ändern
              </Button>
            </Link>

            {customer.twoFactorEnabled ? (
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium text-green-900">
                    2FA aktiviert
                  </span>
                </div>
                <Button variant="outline" size="sm" onClick={() => setTwoFaSetup(null)} disabled={twoFaLoading}>
                  Verwalten
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-muted border border-border rounded-lg">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    2FA nicht aktiviert
                  </span>
                </div>
                <Button variant="outline" size="sm" onClick={start2FASetup} disabled={twoFaLoading}>
                  {twoFaLoading ? '...' : 'Aktivieren'}
                </Button>
              </div>
            )}

            {twoFaSetup && !customer.twoFactorEnabled && (
              <div className="space-y-3 p-3 border rounded-lg bg-blue-50">
                <div className="text-sm font-medium text-foreground">Authenticator-App verbinden</div>
                {twoFaSetup.qrCode && (
                  <div className="flex justify-center">
                    <img src={twoFaSetup.qrCode} alt="2FA QR" className="border p-2 bg-background rounded" />
                  </div>
                )}
                {twoFaSetup.secret && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Manueller Code</div>
                    <code className="block p-2 bg-background border rounded text-sm font-mono">{twoFaSetup.secret}</code>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="twofa-code">6-stelliger Code</Label>
                  <Input
                    id="twofa-code"
                    type="text"
                    inputMode="numeric"
                    placeholder="123456"
                    value={twoFaCode}
                    onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    disabled={twoFaLoading}
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={enable2FA} disabled={twoFaLoading}>Aktivieren</Button>
                  <Button variant="outline" onClick={() => setTwoFaSetup(null)} disabled={twoFaLoading}>Abbrechen</Button>
                </div>
              </div>
            )}

            {customer.twoFactorEnabled && (
              <div className="space-y-2 p-3 border rounded-lg">
                <div className="text-sm font-medium text-red-600">2FA deaktivieren</div>
                <div className="text-sm text-muted-foreground">Zum Deaktivieren Passwort bestätigen.</div>
                <Input
                  type="password"
                  placeholder="Passwort"
                  value={twoFaDisablePassword}
                  onChange={(e) => setTwoFaDisablePassword(e.target.value)}
                  disabled={twoFaLoading}
                />
                <Button variant="destructive" onClick={disable2FA} disabled={twoFaLoading}>Deaktivieren</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete Account */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-red-900">Gefahrenzone</CardTitle>
            <CardDescription>
              Account unwiderruflich löschen
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!showDeleteConfirm ? (
              <Button
                variant="destructive"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Account löschen
              </Button>
            ) : (
              <div className="space-y-4 p-4 border-2 border-red-300 rounded-lg bg-red-50">
                <Alert variant="destructive">
                  <AlertDescription>
                    <strong>Achtung!</strong> Diese Aktion kann nicht rückgängig gemacht werden.
                    Alle deine Daten, Stempelkarten und Codes werden unwiderruflich gelöscht.
                  </AlertDescription>
                </Alert>

                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="delete-confirm">
                    Gib <strong>LÖSCHEN</strong> ein um fortzufahren:
                  </Label>
                  <Input
                    id="delete-confirm"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="LÖSCHEN"
                    disabled={loading}
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="destructive"
                    onClick={handleDeleteAccount}
                    disabled={loading || deleteConfirmText !== 'LÖSCHEN'}
                  >
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Account endgültig löschen
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmText('');
                      setError(null);
                    }}
                    disabled={loading}
                  >
                    Abbrechen
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
